/**
 * 会议门户常量（对接 meeting-portal-service）。
 */
/** 日志模块 */
export const PORTAL_LOG_MODULE = 'Portal';
/** localStorage：门户会话 accessToken */
export const STORAGE_ACCESS_TOKEN = 'jitsi.portal.accessToken';
/** localStorage：门户用户资料 JSON */
export const STORAGE_USER_PROFILE = 'jitsi.portal.userProfile';
/** 直链换票查询参数（tokenAuthUrl 回跳到欢迎页时携带） */
export const PORTAL_AUTH_QUERY = 'portalAuth';
/** 直链换票开启标记值 */
export const PORTAL_AUTH_QUERY_VALUE = '1';
/** URL 中的房间参数名（与 tokenAuthUrl {room} 对应） */
export const PORTAL_AUTH_ROOM_QUERY = 'room';
/** URL 中的 state 参数名 */
export const PORTAL_AUTH_STATE_QUERY = 'state';
/** 手机号校验 */
export const PHONE_PATTERN = /^1\d{10}$/;
/** 短信冷却秒数 */
export const SMS_COOLDOWN_SECONDS = 60;
/** 短信验证码最大长度 */
export const SMS_CODE_MAX_LENGTH = 8;
/** 密码最小长度（与门户注册接口一致） */
export const PASSWORD_MIN_LENGTH = 6;
/** 显示名最大长度（与 users.display_name 一致） */
export const DISPLAY_NAME_MAX_LENGTH = 64;
/** 个性签名最大长度 */
export const SIGNATURE_MAX_LENGTH = 200;
/** AI 小助手单次提问最大字数 */
export const AI_ASSISTANT_INPUT_MAX_LENGTH = 2000;
/** AI 小助手发给后端的最大历史轮数（含本轮提问） */
export const AI_ASSISTANT_HISTORY_MAX = 20;
/** 流式气泡每帧追加字数（60fps 下约 120 字/秒，避免整段一次性出现） */
export const AI_ASSISTANT_REVEAL_CHARS_PER_FRAME = 2;
/** 头像压缩边长 */
export const AVATAR_MAX_EDGE_PX = 512;
/** 背景图压缩最大宽 */
export const BACKGROUND_MAX_EDGE_PX = 1200;
/** 通知轮询间隔（毫秒） */
export const NOTIFICATION_POLL_INTERVAL_MS = 30000;
/** 门户手机 Web 断点（与 _portal.scss max-width: 767px 对齐） */
export const PORTAL_MOBILE_MAX_WIDTH_PX = 767;
/** 默认会议号长度（可被 config.portalMeetingNoLength 覆盖） */
export const DEFAULT_MEETING_NO_LENGTH = 10;
/** JWT 查询参数名（回跳会议室） */
export const JWT_QUERY_PARAM = 'jwt';
/** 门户业务错误码：同账号已在会中（与 meeting-portal-service 对齐） */
export const ERROR_CODE_ALREADY_IN_MEETING = 'ALREADY_IN_MEETING';
/** 门户业务错误码：同账号并发设备已达上限 */
export const ERROR_CODE_DEVICE_LIMIT_EXCEEDED = 'DEVICE_LIMIT_EXCEEDED';
/** 门户业务错误码：会议已结束（与 meeting-portal-service 对齐） */
export const ERROR_CODE_MEETING_ENDED = 'MEETING_ENDED';
/** 门户业务错误码：会议不存在 */
export const ERROR_CODE_MEETING_NOT_FOUND = 'MEETING_NOT_FOUND';
/** Hash：入会开启屏幕共享 */
export const HASH_START_SCREEN_SHARING = 'config.startWithScreenSharing=true';
/**
 * Hash：关闭预加入页（与 Electron Conference configOverwrite.prejoinConfig.enabled=false 对齐）。
 * 门户入会后直接进会议室，不再出现「加入会议」确认页。
 */
export const HASH_PREJOIN_DISABLED = 'config.prejoinConfig.enabled=false';
/**
 * Hash：多设备模式 MULTI。
 * 值必须是合法 JSON 字符串（带引号），否则 parseURLParams 会 JSON.parse 失败并刷屏报错。
 */
export const HASH_MULTI_DEVICE_MODE_MULTI = 'config.multiDeviceMode="MULTI"';
/**
 * Hash：多设备模式 SINGLE + 踢旧。
 * 值必须是合法 JSON 字符串（带引号）。
 */
export const HASH_MULTI_DEVICE_MODE_SINGLE = 'config.multiDeviceMode="SINGLE"';
/** Hash：入会 replaceParticipant（flip_device） */
export const HASH_REPLACE_PARTICIPANT = 'config.replaceParticipant=true';
/** Hash：预定勾选后主持人进会自动云录制 */
export const HASH_AUTO_CLOUD_RECORDING = 'config.autoCloudRecording=true';
/**
 * Hash：成员入会静音（Jitsi startAudioMuted 人数阈值）。
 *
 * @param {number} startAudioMuted - 超过该人数后入会静音。
 * @returns {string}
 */
export function hashStartAudioMuted(startAudioMuted) {
    return `config.startAudioMuted=${startAudioMuted}`;
}
/** Hash：入会麦克风静音 */
export const HASH_START_WITH_AUDIO_MUTED = 'config.startWithAudioMuted=true';
/** Hash：入会摄像头关闭 */
export const HASH_START_WITH_VIDEO_MUTED = 'config.startWithVideoMuted=true';
/** Hash：入会扬声器静音 */
export const HASH_START_SILENT = 'config.startSilent=true';
/** localStorage：记住加入会议显示名 */
export const STORAGE_JOIN_DISPLAY_NAME = 'portal.joinDisplayName';
/** localStorage：是否记住加入会议显示名 */
export const STORAGE_REMEMBER_JOIN_NAME = 'portal.rememberJoinName';
/** localStorage：加入会议号下拉历史 */
export const STORAGE_JOIN_MEETING_NO_HISTORY = 'portal.joinMeetingNoHistory';
/** 加入会议号下拉最多保留条数 */
export const JOIN_MEETING_NO_HISTORY_MAX = 20;
/** 默认门户 API 基址（仅开发兜底；正式环境用 config.portalApiBaseUrl） */
export const DEFAULT_PORTAL_API_BASE_URL = 'http://127.0.0.1:8088';
/** 默认应用名 */
export const DEFAULT_PORTAL_APP_NAME = '会议门户';
/** 录制详情态右侧会议列表宽度（像素，与 _portal.scss 对齐） */
export const RECORDINGS_DETAIL_SIDE_WIDTH_PX = 320;
/** localStorage：门户主题偏好（与 jitsi-meet / Electron THEME_MODES 一致） */
export const STORAGE_THEME_MODE = 'jitsi.portal.themeMode';
/** sessionStorage：登录取消了冷静期注销，首页提示一次 */
export const STORAGE_DELETION_CANCELLED = 'jitsi.portal.deletionCancelled';
/**
 * sessionStorage：当前会中门户会议 ID。
 * 入会时写入，结束/离开后清除，供 endConference 调门户 /end。
 */
export const STORAGE_ACTIVE_MEETING_ID = 'jitsi.portal.activeMeetingId';
/** 最近浏览录制 ID */
export const STORAGE_RECORDING_RECENT_IDS = 'jitsi.portal.recordingRecentIds';
/** 最近浏览最多保留条数 */
export const RECORDINGS_RECENT_IDS_MAX = 50;
/**
 * 界面主题选项（与 jitsi-meet base/settings/constants.ts 同源，保持一致）。
 */
export const THEME_MODES = {
    DARK: 'dark',
    LIGHT: 'light',
    SYSTEM: 'system'
};
